import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, XCircle } from "lucide-react";
import { ActivityDot } from "@/components/atoms/testing/ActivityDot";
import type { TestingChatActivity } from "@/stores/testingStore";
import { activityKindLabel, formatActivityTime } from "@/utils/testing/planDisplay";
import { ThinkingSkeleton } from "./ShimmerCaption";

/** Rows kept in the expanded trail. Older ones are dropped, newest first. */
const VISIBLE_TRAIL_ROWS = 12;
/** Auto-follow the trail only while the reader is already near its bottom. */
const FOLLOW_THRESHOLD_PX = 60;

export interface ThinkingPanelProps {
	/** The turn is live — show the pulse, the caption and the running row. */
	streaming: boolean;
	/** Coarse stage label from the planner ("Researching your org", "Plan ready for review"). */
	phase: string | null;
	activity: TestingChatActivity[];
	/** Collapsed by default for a finished turn, expanded while live. */
	defaultOpen?: boolean;
}

/**
 * What the agent is doing, in one place.
 *
 * The chat previously showed live progress as a bordered card listing up to twelve raw event rows —
 * every tool call, at full weight, competing with the answer itself. This is the same information
 * with a hierarchy: the current step in prose, and the full trail one disclosure away.
 *
 * The left accent rule (rather than a full card) is Weave's Narration treatment, adopted because it
 * reads as "in progress" without adding another box to a thread that already has plan and result
 * cards. Everything else — Testing's activity kinds, the finished-turn collapsed state — is Testing's.
 */
export function ThinkingPanel({ streaming, phase, activity, defaultOpen }: ThinkingPanelProps) {
	// Open while live, collapsed once finished. Watching the agent work is the whole point of the
	// panel during a turn; afterwards it is reference material and must not bury the answer.
	// A manual toggle sticks — `userToggled` stops the auto-behaviour from fighting the click.
	const [userToggled, setUserToggled] = useState(false);
	const [open, setOpen] = useState(defaultOpen ?? streaming);
	useEffect(() => {
		if (!userToggled) setOpen(streaming);
	}, [streaming, userToggled]);
	const trailRef = useRef<HTMLDivElement>(null);

	// Substantive work only: phase/message rows are narration, not steps, and counting them made the
	// step number jump by two whenever the planner spoke.
	const steps = useMemo(
		() => activity.filter((a) => a.kind !== "phase" && a.kind !== "message"),
		[activity],
	);
	const latest = activity[activity.length - 1];
	const headline = streaming
		? latest?.label || phase || "Reading your org"
		: phase || "What the agent did";

	// Outcome of the turn, surfaced in the COLLAPSED header. Without it the summary reads
	// "Details · 4 steps" whether the run sailed through or a tool errored, so the one fact worth
	// knowing before expanding was the one fact hidden.
	const hasError = useMemo(() => activity.some((a) => a.status === "error"), [activity]);
	const hasBlocked = useMemo(() => activity.some((a) => a.status === "blocked"), [activity]);

	// Follow the trail as rows arrive, but never yank a reader who has scrolled up inside it.
	useEffect(() => {
		const el = trailRef.current;
		if (!el || !open) return;
		const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
		if (distanceFromBottom > FOLLOW_THRESHOLD_PX) return;
		const frame = requestAnimationFrame(() => {
			if (trailRef.current) trailRef.current.scrollTop = trailRef.current.scrollHeight;
		});
		return () => cancelAnimationFrame(frame);
	}, [activity.length, open]);

	// Nothing to say: a finished turn that recorded no tool work needs no disclosure at all.
	if (!streaming && steps.length === 0) return null;

	const rows = activity.slice(-VISIBLE_TRAIL_ROWS).reverse();

	return (
		<div className="tst-thinking tst-fade-up" aria-live={streaming ? "polite" : "off"}>
			<div className="flex min-w-0 items-center gap-2">
				{streaming ? (
					<span className="relative flex h-1.5 w-1.5 shrink-0">
						<span
							data-tst-decorative-motion
							className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-70"
						/>
						<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
					</span>
				) : hasError ? (
					<XCircle className="h-3.5 w-3.5 shrink-0 text-destructive-strong" />
				) : hasBlocked ? (
					<AlertTriangle className="h-3.5 w-3.5 shrink-0 text-primary" />
				) : (
					<CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success-strong" />
				)}
				<span className="tst-micro shrink-0">{streaming ? "Working" : "Details"}</span>
				<span className="min-w-0 flex-1 truncate text-[12px] text-muted-foreground">{headline}</span>
				{/* Outcome chips: legible at a glance while the trail stays collapsed. */}
				{!streaming && hasBlocked && (
					<span className="shrink-0 rounded border border-primary/30 bg-primary/5 px-1.5 py-0.5 text-[11px] font-medium text-primary">
						blocked
					</span>
				)}
				{!streaming && hasError && (
					<span className="shrink-0 rounded border border-destructive/30 bg-destructive/5 px-1.5 py-0.5 text-[11px] font-medium text-destructive-strong">
						error
					</span>
				)}
				{steps.length > 0 && (
					<button
						type="button"
						onClick={() => {
							setUserToggled(true);
							setOpen((v) => !v);
						}}
						aria-expanded={open}
						className="tst-interactive inline-flex min-h-[24px] shrink-0 items-center gap-0.5 rounded px-1.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
					>
						{open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
						{steps.length} step{steps.length === 1 ? "" : "s"}
					</button>
				)}
			</div>

			{/* Warm-up: a live turn that has produced no steps yet still needs to look alive. */}
			{streaming && steps.length === 0 && (
				<div className="mt-2.5">
					<ThinkingSkeleton />
				</div>
			)}

			{open && steps.length > 0 && (
				<div className="relative mt-2">
					<div ref={trailRef} className="max-h-52 space-y-1 overflow-y-auto pr-1">
						{rows.map((entry) => (
							<ActivityTrailRow
								key={entry.id}
								entry={entry}
								streaming={streaming && entry === latest}
							/>
						))}
					</div>
					{/* Fade the cut-off edge so a scrolled trail reads as continuing, not as truncated. */}
					<div
						aria-hidden
						className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-b from-transparent to-[var(--tst-surface-soft)]"
					/>
				</div>
			)}
		</div>
	);
}

/** Row tints for the states worth finding in a long trail; everything else stays quiet. */
const ROW_TONE: Partial<Record<NonNullable<TestingChatActivity["status"]>, string>> = {
	blocked: "border-primary/30 bg-primary/5",
	warning: "border-warning/30 bg-warning/5",
	error: "border-destructive/30 bg-destructive/5",
};

/**
 * One row of the activity trail, with its detail expandable.
 *
 * A tool result can be long — an IR-validation rejection runs to paragraphs — and a hard two-line
 * clamp left no way to read the rest. The expand control appears only when the clamp is ACTUALLY
 * hiding text, measured against the rendered box, so a one-line detail doesn't get a useless toggle.
 *
 * Ported from Shrey's `fix/testing-activity-collapse` (1d9dee15), which added this to the component
 * this panel replaced. The measurement approach is his; the sizing here meets the module's 24px
 * minimum target size, and the tone tokens are the theme-safe ones.
 */
function ActivityTrailRow({
	entry,
	streaming,
}: {
	entry: TestingChatActivity;
	streaming: boolean;
}) {
	const [expanded, setExpanded] = useState(false);
	const [clamped, setClamped] = useState(false);
	const detailRef = useRef<HTMLParagraphElement>(null);

	// Re-measure when the text changes or when we collapse back to the clamped layout.
	useEffect(() => {
		const el = detailRef.current;
		if (!el || expanded) return;
		setClamped(el.scrollHeight > el.clientHeight + 1);
	}, [entry.detail, expanded]);

	const tone = entry.status ? ROW_TONE[entry.status] : undefined;

	return (
		<div
			className={`flex items-start gap-2 rounded-sm px-1.5 py-1 ${tone ? `border ${tone}` : ""}`}
		>
			<ActivityDot status={entry.status} streaming={streaming} />
			<div className="min-w-0 flex-1">
				<div className="flex min-w-0 items-baseline gap-1.5">
					<span className="shrink-0 font-mono text-[11px] uppercase tracking-wide text-muted-foreground/70">
						{activityKindLabel(entry.kind)}
					</span>
					<span className="min-w-0 flex-1 truncate text-[12px] text-foreground/85">{entry.label}</span>
					<span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground/60">
						{formatActivityTime(entry.at)}
					</span>
					{entry.detail && (clamped || expanded) && (
						<button
							type="button"
							onClick={() => setExpanded((v) => !v)}
							aria-expanded={expanded}
							aria-label={expanded ? "Collapse details" : "Show full details"}
							className="tst-interactive inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground/70 hover:bg-muted hover:text-foreground"
						>
							<ChevronDown
								className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`}
							/>
						</button>
					)}
				</div>
				{entry.detail && (
					<p
						ref={detailRef}
						className={`mt-0.5 font-mono text-[11px] leading-snug text-muted-foreground ${
							expanded ? "whitespace-pre-wrap [overflow-wrap:anywhere]" : "line-clamp-2"
						}`}
					>
						{entry.detail}
					</p>
				)}
			</div>
		</div>
	);
}

/**
 * The strip shown UNDER a landed answer while the run keeps going. Motion carries the signal; the
 * step label is secondary. Weave's "still working" pattern, adopted because Testing has the same
 * gap — the plan arrives, then the browser run continues for minutes.
 */
export function StillWorking({ label }: { label: string }) {
	return (
		<div className="tst-fade-up flex items-center gap-2.5" aria-live="polite">
			<span className="relative flex h-1.5 w-1.5 shrink-0">
				<span
					data-tst-decorative-motion
					className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-70"
				/>
				<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
			</span>
			<span
				data-tst-decorative-motion
				className="h-1.5 max-w-[180px] flex-1 animate-pulse rounded-full bg-muted"
			/>
			<span className="tst-micro shrink-0 normal-case tracking-normal">{label}</span>
		</div>
	);
}
